import type { ResourceBindingContext } from '@seen/core/resourceBindingRegistry'
import { RegistryVariableNode as RegistryVariableSchema } from '@seen/core/tiptap/schema'
import type { Node as PMNode } from '@tiptap/pm/model'
import { Plugin, PluginKey } from '@tiptap/pm/state'
import { ReactNodeViewRenderer } from '@tiptap/react'
import {
  changedRanges,
  convertLegacyTokens,
  mapScope,
  REGISTRY_VARIABLE_SCAN_META,
  resolveDeferredVariables,
  type ScanScope,
} from '#src/components/rich-text-editor/registry-variable/registryVariableResolution.ts'
import { RegistryVariableNodeView } from '#src/components/rich-text-editor/registry-variable/RegistryVariableView.tsx'
import { resilientNodeView } from '#src/components/rich-text-editor/ResilientNodeView.tsx'

export interface RegistryVariableOptions {
  // A template keeps every variable deferred.
  isTemplate: boolean
  // Seeds the contexts at construction. Contexts that arrive later go through
  // setRegistryVariableContexts, because useEditor never rebuilds extensions.
  contexts: ResourceBindingContext
}

export interface RegistryVariableStorage {
  contexts: ResourceBindingContext
  // A scan was skipped while the editor was read-only; run it once editable.
  scanPending: boolean
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    registryVariable: {
      setRegistryVariableContexts: (
        contexts: ResourceBindingContext
      ) => ReturnType
    }
  }
  interface Storage {
    registryVariable?: RegistryVariableStorage
  }
}

export const RegistryVariableNode = RegistryVariableSchema.extend<
  RegistryVariableOptions,
  RegistryVariableStorage
>({
  addOptions() {
    return {
      isTemplate: false,
      contexts: {},
    }
  },

  addStorage() {
    return {
      contexts: this.options.contexts,
      scanPending: false,
    }
  },

  addNodeView() {
    return ReactNodeViewRenderer(
      resilientNodeView(RegistryVariableNodeView, { inline: true }),
      { as: 'span' }
    )
  },

  addCommands() {
    return {
      setRegistryVariableContexts:
        (contexts) =>
        ({ tr, dispatch }) => {
          this.storage.contexts = contexts
          if (dispatch) {
            tr.setMeta(REGISTRY_VARIABLE_SCAN_META, true)
            tr.setMeta('addToHistory', false)
          }
          return true
        },
    }
  },

  // A document with nothing to resolve gets no mount transaction, so hosts
  // that count editor updates (content sync) see a quiet mount.
  onCreate() {
    if (!docNeedsRegistryScan(this.editor.state.doc, this.name)) {
      return
    }
    this.editor.view.dispatch(
      this.editor.state.tr
        .setMeta(REGISTRY_VARIABLE_SCAN_META, true)
        .setMeta('addToHistory', false)
    )
  },

  // setEditable emits an update without dispatching a transaction, so a scan
  // skipped while read-only is retried here.
  onUpdate() {
    if (!this.storage.scanPending || !this.editor.isEditable) {
      return
    }
    this.storage.scanPending = false
    this.editor.view.dispatch(
      this.editor.state.tr
        .setMeta(REGISTRY_VARIABLE_SCAN_META, true)
        .setMeta('addToHistory', false)
    )
  },

  addProseMirrorPlugins() {
    const { editor, options, storage } = this
    return [
      new Plugin({
        key: new PluginKey('registryVariable'),
        appendTransaction: (transactions, oldState, newState) => {
          const fullScan = transactions.some((tr) =>
            tr.getMeta(REGISTRY_VARIABLE_SCAN_META)
          )
          if (!fullScan && !transactions.some((tr) => tr.docChanged)) {
            return null
          }
          // A read-only viewer must never write into a shared note.
          if (!editor.isEditable) {
            storage.scanPending = true
            return null
          }
          const scope: ScanScope = fullScan
            ? 'all'
            : changedRanges(oldState.doc, transactions)
          const tr = newState.tr
          convertLegacyTokens(tr, scope)
          // A full scan follows a context change or the editor opening, so
          // everything is brought up to date, not only what was deferred.
          if (!options.isTemplate) {
            resolveDeferredVariables(
              tr,
              storage.contexts,
              mapScope(scope, tr.mapping),
              new Date().toISOString(),
              fullScan
            )
          }
          return tr.docChanged ? tr : null
        },
      }),
    ]
  },
})

function docNeedsRegistryScan(doc: PMNode, typeName: string): boolean {
  let needed = false
  doc.descendants((node) => {
    if (needed) {
      return false
    }
    if (
      node.type.name === typeName ||
      (node.isText && node.text?.includes('{{'))
    ) {
      needed = true
      return false
    }
    return true
  })
  return needed
}
